import { Platform as AngularPlatform } from '@angular/cdk/platform';
import { Inject, Injectable } from '@angular/core';
import { Platform as IonicPlatform } from '@ionic/angular';
import { VersionCheckConfig } from 'ag-common-lib/public-api';
import { FIREBASE_APP, VersionCheckService } from 'ag-common-svc/public-api';
import { FirebaseApp } from 'firebase/app';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, Observable, from } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { getFirebasePreviewChannelId, isFirebasePreviewHost } from '../utils/firebase-preview-host.util';

@Injectable({
  providedIn: 'root',
})
export class AppPlatformService extends VersionCheckService {
  readonly platformId: string;
  readonly isIos: boolean;
  readonly isAndroid: boolean;
  readonly isSafari: boolean;
  readonly browser: string;

  isPlatformReady$ = from(this.ionicPlatform.ready());

  private readonly _isPWA$ = new BehaviorSubject<boolean>(false);
  isPWA$: Observable<boolean> = this._isPWA$.asObservable();

  private readonly _isLandscape$ = new BehaviorSubject<boolean>(false);
  isLandscape$: Observable<boolean> = this._isLandscape$.asObservable();

  private readonly _isPortrait$ = new BehaviorSubject<boolean>(false);
  isPortrait$: Observable<boolean> = this._isPortrait$.asObservable();

  constructor(
    angularPlatform: AngularPlatform,
    private ionicPlatform: IonicPlatform,
    public deviceService: DeviceDetectorService,
    @Inject(FIREBASE_APP) firebaseApp: FirebaseApp,
  ) {
    super(firebaseApp);

    this.platformId = this.getPlatformId();
    this.isIos = angularPlatform.IOS;
    this.isAndroid = angularPlatform.ANDROID;
    const { browser } = deviceService.getDeviceInfo();
    this.isSafari = /safari/i.test(browser ?? '');
    this.browser = browser;
    console.log('browser', browser);
    void this.bootstrap();
  }

  protected override async onVersionAccepted(nextVersion: string): Promise<void> {
    // Force a genuinely fresh load rather than depending on the service-worker update
    // lifecycle: clear every Cache Storage entry and unregister the worker so the reload is
    // fetched from the network. A plain reload while the old worker still controls the tab is
    // served the stale, cached app; waiting on swUpdate.activateUpdate() only helps once the
    // new version has finished downloading. The worker re-registers on the next load.
    await this.clearServiceWorkerCaches();

    await super.onVersionAccepted(nextVersion);
  }

  /** Drop all Cache Storage entries and unregister any service worker so the next load is
   *  served from the network. Best-effort — never block the reload if it fails. */
  private async clearServiceWorkerCaches(): Promise<void> {
    try {
      if (typeof caches !== 'undefined') {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map(key => caches.delete(key)));
      }

      if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(registration => registration.unregister()));
      }
    } catch (error) {
      console.warn('[app-platform] failed to clear caches before reload, reloading anyway', error);
    }
  }

  private async bootstrap() {
    await this.init();
    this.startVersionChecks(this.getVersionCheckConfig());
  }

  private async init() {
    let platformId = localStorage.getItem('platformId');
    if (!platformId) {
      platformId = '';
      localStorage.setItem('platformId', platformId);
    }

    await this.ionicPlatform.ready();

    this._isPWA$.next(this.ionicPlatform.is('pwa'));
    this.checkDeviceOrientation();

    this.ionicPlatform.resize.subscribe(() => {
      this.checkDeviceOrientation();
    });
  }

  private getVersionCheckConfig(): VersionCheckConfig {
    const isPreviewHost = isFirebasePreviewHost();
    const previewChannelId = getFirebasePreviewChannelId();
    const baseDocumentId = environment?.versionSignal?.firestoreDocumentId ?? 'ag-portal-version';
    const baseStorageKey = environment?.versionSignal?.storageKey ?? 'ag_portal_web_version';
    const baseSnoozeStorageKey = environment?.versionSignal?.snoozeStorageKey ?? 'ag_portal_web_version_snooze_until';
    const previewSuffix = previewChannelId ? `_preview_${previewChannelId}` : '_preview';

    return {
      enabled: environment?.versionSignal?.enabled ?? true,
      firestoreCollection: environment?.versionSignal?.firestoreCollection ?? 'system',
      firestoreDocumentId: isPreviewHost && previewChannelId ? `${baseDocumentId}-preview-${previewChannelId}` : baseDocumentId,
      storageKey: isPreviewHost
        ? `${baseStorageKey}${previewSuffix}`
        : baseStorageKey,
      snoozeStorageKey: isPreviewHost
        ? `${baseSnoozeStorageKey}${previewSuffix}`
        : baseSnoozeStorageKey,
      fallbackPollMs: environment?.versionSignal?.fallbackPollMs ?? 1000 * 60,
      afterDeclinePollMs: environment?.versionSignal?.afterDeclinePollMs ?? 1000 * 60,
    };
  }

  private getPlatformId(): string {
    let platformId = localStorage.getItem('platformId');
    if (!platformId) {
      platformId = crypto.randomUUID();
      localStorage.setItem('platformId', platformId);
    }

    return platformId;
  }

  private checkDeviceOrientation() {
    this._isLandscape$.next(this.ionicPlatform.isLandscape());
    this._isPortrait$.next(this.ionicPlatform.isPortrait());
  }
}
